import { useState, useEffect, useCallback } from "react";
import { apiV2Service } from "../../../../services/apiV2Service";
import logger from "../../../../utils/logger";
import { getCurrentUserOrganizationType, getCurrentUserOrganizationId } from "../utils/hostClientsUtils";
import { FeatureAccess } from "../../../../utils/featureAccessControl";
import { getUser } from "../../../../services/localService";

export const useRoleSections = (organizationTypes, organizationRoles) => {
  // Dynamic role sections states
  const [roleSections, setRoleSections] = useState({});
  const [currentUserOrgType, setCurrentUserOrgType] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState({});

  // Set up role sections based on current user's organization type
  const setupRoleSections = useCallback(() => {
    const userOrgType = getCurrentUserOrganizationType();
    logger.log("Setting up role sections for organization type:", userOrgType);

    if (!organizationRoles.length) {
      logger.warn("Cannot setup role sections - no roles available");
      return;
    }

    setCurrentUserOrgType(userOrgType);

    // Filter roles based on user's organization type, with fallback
    let applicableRoles = [];

    if (userOrgType === 2) {
      // HOST_PROPERTY_DISTRIBUTION roles - these users manage their own organization users only
      applicableRoles = organizationRoles.filter(
        role => role.organizationTypeId === 2 && (role.code === "ROLE_HOST" || role.code === "ROLE_HOST_OPERATIONS")
      );
    } else if (userOrgType === 3) {
      // HOST_PROPERTY_MANAGEMENT roles - these users can manage client organizations and their users
      // Individual customers now belong to organization type 7, but are managed by type 3 hosts
      applicableRoles = organizationRoles.filter(
        role =>
          (role.organizationTypeId === 3 &&
            (role.code === "ROLE_SUBSCRIBER_HOST" || role.code === "ROLE_SUBSCRIBER_HOST_OPERATIONS")) ||
          (role.organizationTypeId === 7 && role.code === "ROLE_HOST_CUSTOMER_INDIVIDUAL")
      );
    } else if (userOrgType === 4) {
      // CORPORATE_CUSTOMER roles - these users manage their own organization users only
      applicableRoles = organizationRoles.filter(role => role.organizationTypeId === 4);
    } else if (userOrgType === 6) {
      // HOST_CORPORATE_CUSTOMER roles - these users manage their own organization users only
      applicableRoles = organizationRoles.filter(role => role.organizationTypeId === 6);
    } else {
      // Fallback: if we can't determine org type, show all roles
      logger.warn("Unknown organization type, showing all available roles");
      applicableRoles = organizationRoles.filter(
        role =>
          role.code === "ROLE_HOST" ||
          role.code === "ROLE_HOST_OPERATIONS" ||
          role.code === "ROLE_SUBSCRIBER_HOST" ||
          role.code === "ROLE_SUBSCRIBER_HOST_OPERATIONS" ||
          role.code === "ROLE_HOST_CUSTOMER_INDIVIDUAL"
      );
    }

    const currentUser = getUser();
    const showStaffSections = FeatureAccess.hasStaffTabAccess(currentUser);

    // Further filter roles based on feature access rules
    applicableRoles = applicableRoles.filter(role => {
      // If it's a staff role, check if user has staff access
      const isStaffRole = [
        "ROLE_HOST",
        "ROLE_HOST_OPERATIONS",
        "ROLE_SUBSCRIBER_HOST",
        "ROLE_SUBSCRIBER_HOST_OPERATIONS",
      ].includes(role.code);

      if (isStaffRole && !showStaffSections) {
        return false;
      }

      return true;
    });

    logger.log("Applicable roles for org type", userOrgType, ":", applicableRoles);
    setAvailableRoles(applicableRoles);

    // Initialize role sections state
    const initialSections = {};
    applicableRoles.forEach(role => {
      initialSections[role.code] = {
        open: false,
        loading: true, // Start with loading true for initial load
        error: null,
        users: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          page: 1,
          limit: 10,
        },
        currentStatus: "ACTIVE",
        searchTerm: "",
      };
    });

    setRoleSections(initialSections);
    logger.log("Initialized role sections:", initialSections);
  }, [organizationRoles, organizationTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search users using the search API
  const searchUsers = async (searchQuery, isOwner = false, page = 1, limit = 10) => {
    logger.log(`Searching users with query: "${searchQuery}", isOwner: ${isOwner}, page: ${page}`);

    try {
      const response = await apiV2Service.searchUsers({
        q: searchQuery,
        isOwner,
        page,
        limit,
        status: "ACTIVE",
      });

      logger.log("User search response:", response);
      return response;
    } catch (error) {
      logger.error("Error searching users:", error);
      throw error;
    }
  };

  // Search users and filter by role
  const searchUsersByRole = async (roleCode, searchQuery, status = "active", page = 1, limit = 10) => {
    logger.log(`Searching users for role: ${roleCode}, query: "${searchQuery}", status: ${status}`);

    // Update loading state for this specific role section
    setRoleSections(prev => ({
      ...prev,
      [roleCode]: {
        ...prev[roleCode],
        loading: true,
        error: null,
      },
    }));

    try {
      // Find the role object to get its ID
      const role = organizationRoles.find(r => r.code === roleCode);
      if (!role) {
        throw new Error(`Role not found: ${roleCode}`);
      }

      // Search users
      const searchResponse = await searchUsers(searchQuery, false, page, limit);

      // Filter users by role and status
      const filteredUsers = searchResponse.data.filter(user => {
        const matchesRole = user.organizationRoleId === role.id;
        const matchesStatus = status === "all" || user.status.toLowerCase() === status.toLowerCase();
        return matchesRole && matchesStatus;
      });

      // Update the role section with search results
      setRoleSections(prev => ({
        ...prev,
        [roleCode]: {
          ...prev[roleCode],
          loading: false,
          users: filteredUsers,
          searchTerm: searchQuery,
          pagination: {
            totalCount: filteredUsers.length,
            totalPages: Math.ceil(filteredUsers.length / limit),
            page: page,
            limit: limit,
          },
        },
      }));

      logger.log(`Found ${filteredUsers.length} users for role ${roleCode}`);
    } catch (error) {
      logger.error(`Error searching users for role ${roleCode}:`, error);

      setRoleSections(prev => ({
        ...prev,
        [roleCode]: {
          ...prev[roleCode],
          loading: false,
          error: error.message || `Failed to search users for ${roleCode}`,
        },
      }));
    }
  };

  // Fetch users for a specific role section
  const fetchRoleSectionUsers = useCallback(
    async (roleCode, status = "ACTIVE", page = 1, limit = 10) => {
      logger.log(`Fetching users for role: ${roleCode}, status: ${status}, page: ${page}`);

      // Prevent multiple simultaneous calls for the same role
      if (roleSections[roleCode]?.loading) {
        logger.log(`Already loading ${roleCode}, skipping...`);
        return;
      }

      // Update loading state for this specific role section
      setRoleSections(prev => ({
        ...prev,
        [roleCode]: {
          ...prev[roleCode],
          loading: true,
          error: null,
        },
      }));

      try {
        const currentUserOrgId = getCurrentUserOrganizationId();
        if (!currentUserOrgId) {
          throw new Error("No organization ID found for current user");
        }

        // Find the role object to get its ID
        const role = organizationRoles.find(r => r.code === roleCode);
        if (!role) {
          throw new Error(`Role not found: ${roleCode}`);
        }

        logger.log(`Fetching users for role ${roleCode} (${role.name}) with ID: ${role.id}`);

        const isIndividualCustomer = role.code === "ROLE_HOST_CUSTOMER_INDIVIDUAL";

        // Only pass the clients parameter for Host Property Management (org type 3)
        // Host Property Distribution (org type 2) should NOT pass clients parameter
        const userOrgType = getCurrentUserOrganizationType();
        const shouldPassClientsParam = userOrgType === 3; // Only for HOST_PROPERTY_MANAGEMENT

        // Build params object - only include mode if it should be passed
        const modeParam = shouldPassClientsParam && isIndividualCustomer ? "CLIENTS" : undefined;

        // IMPORTANT: For individual customers, do NOT pass organizationId or organizationTypeId
        // Backend requires: mode=CLIENTS (without organizationId and organizationTypeId)
        const orgIdParam = isIndividualCustomer ? null : currentUserOrgId;
        const orgTypeIdParam = isIndividualCustomer ? null : role.organizationTypeId;

        const response = await apiV2Service.getUsersByOrganizationType(
          orgTypeIdParam,
          status === "all" ? null : status,
          page,
          limit,
          orgIdParam, // null for individual customers, currentUserOrgId for others
          isIndividualCustomer ? null : [role.id],
          modeParam // Only pass when set, otherwise undefined (not passed to API)
        );

        logger.log(`API Response for ${roleCode}:`, response.data);

        if (response.status === 200 && response.data) {
          // Handle different API response structures
          const usersData = response.data.data || response.data.content || response.data.users || [];
          const paginationData = response.data.pagination || {};

          logger.log(`${roleCode} users fetched successfully:`, {
            userCount: usersData.length,
            users: usersData,
            firstUser: usersData[0], // Log first user to see structure
            pagination: paginationData,
          });

          setRoleSections(prev => ({
            ...prev,
            [roleCode]: {
              ...prev[roleCode],
              loading: false,
              users: usersData,
              pagination: {
                totalCount: paginationData.totalCount || response.data.totalElements || usersData.length,
                totalPages:
                  paginationData.totalPages || response.data.totalPages || Math.ceil(usersData.length / limit),
                page: paginationData.page || (response.data.number ? response.data.number + 1 : page),
                limit: paginationData.limit || response.data.size || limit,
              },
            },
          }));

          // Mark initial load as complete on success
          setInitialLoadComplete(prev => ({ ...prev, [roleCode]: true }));
        }
      } catch (error) {
        logger.error(`Error fetching ${roleCode} users:`, error);

        // Check if this is a permission/authorization error vs a genuine error
        const isPermissionError =
          error?.response?.status === 403 ||
          error?.response?.status === 401 ||
          error?.message?.includes("No organization ID") ||
          error?.message?.includes("permission") ||
          error?.message?.includes("Forbidden");

        // Only show error if it's NOT a permission error (which is expected for some user types)
        // For HOST_PROPERTY_MANAGEMENT (org type 3), we should see errors
        // For other org types without org ID, this is expected behavior
        const userOrgType = getCurrentUserOrganizationType();
        const shouldShowError = userOrgType === 3 || !isPermissionError;

        setRoleSections(prev => ({
          ...prev,
          [roleCode]: {
            ...prev[roleCode],
            loading: false,
            error: shouldShowError ? error.message || `Failed to fetch ${roleCode} users` : null,
          },
        }));

        // Mark initial load as complete even on error
        setInitialLoadComplete(prev => ({ ...prev, [roleCode]: true }));
      }
    },
    [organizationRoles]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh all role sections
  const refreshAllRoleSections = () => {
    Object.keys(roleSections).forEach(roleCode => {
      const section = roleSections[roleCode];
      fetchRoleSectionUsers(roleCode, section.currentStatus, 1);
    });
  };

  // Update role section status
  const updateRoleSectionStatus = (roleCode, status) => {
    setRoleSections(prev => ({
      ...prev,
      [roleCode]: {
        ...prev[roleCode],
        currentStatus: status,
      },
    }));
  };

  // Update role section search term and trigger search
  const updateRoleSectionSearch = (roleCode, searchTerm) => {
    setRoleSections(prev => ({
      ...prev,
      [roleCode]: {
        ...prev[roleCode],
        searchTerm: searchTerm,
      },
    }));

    // If there's a search term, trigger search
    if (searchTerm && searchTerm.trim().length > 0) {
      const section = roleSections[roleCode];
      if (section) {
        searchUsersByRole(roleCode, searchTerm.trim(), section.currentStatus || "ACTIVE", 1, 10);
      }
    } else {
      // If search term is empty, fetch all users for the role
      fetchRoleSectionUsers(roleCode, roleSections[roleCode]?.currentStatus || "ACTIVE");
    }
  };

  // Toggle role section open/closed
  const toggleRoleSection = roleCode => {
    setRoleSections(prev => ({
      ...prev,
      [roleCode]: {
        ...prev[roleCode],
        open: !prev[roleCode].open,
      },
    }));
  };

  // Setup role sections when organization roles are loaded
  useEffect(() => {
    if (organizationRoles && organizationRoles.length > 0) {
      logger.log("Setting up role sections with roles:", organizationRoles.length);
      setupRoleSections();

      // Trigger initial fetch for each role section immediately after setup
      // Filter roles again to match setupRoleSections logic
      const userOrgType = getCurrentUserOrganizationType();
      let applicableRoles = [];
      if (userOrgType === 2) {
        applicableRoles = organizationRoles.filter(
          role => role.organizationTypeId === 2 && (role.code === "ROLE_HOST" || role.code === "ROLE_HOST_OPERATIONS")
        );
      } else if (userOrgType === 3) {
        applicableRoles = organizationRoles.filter(
          role =>
            (role.organizationTypeId === 3 &&
              (role.code === "ROLE_SUBSCRIBER_HOST" || role.code === "ROLE_SUBSCRIBER_HOST_OPERATIONS")) ||
            (role.organizationTypeId === 7 && role.code === "ROLE_HOST_CUSTOMER_INDIVIDUAL")
        );
      } else if (userOrgType === 4) {
        applicableRoles = organizationRoles.filter(role => role.organizationTypeId === 4);
      } else if (userOrgType === 6) {
        applicableRoles = organizationRoles.filter(role => role.organizationTypeId === 6);
      }

      applicableRoles.forEach(role => {
        fetchRoleSectionUsers(role.code);
      });
    }
  }, [organizationRoles, setupRoleSections, fetchRoleSectionUsers]);

  return {
    roleSections,
    setRoleSections,
    currentUserOrgType,
    availableRoles,
    setupRoleSections,
    fetchRoleSectionUsers,
    searchUsers,
    searchUsersByRole,
    refreshAllRoleSections,
    updateRoleSectionStatus,
    updateRoleSectionSearch,
    toggleRoleSection,
  };
};
